import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
import ItemDetailModal from "../../components/ItemDetailModal";
import ReviewsDisplay from "../../components/ReviewsDisplay"; // Phase G
import LooksGallery from "../../components/LooksGallery";
import ServiceCard from "../../components/ServiceCard";
import FollowButton from "../../components/looks/FollowButton";
import { formatProfessionalType } from "../../utils/formatters";
import { getDirectShopCataloguePath } from "../../utils/shopUrls";
import "./ShopLanding.css";
import "./ProfessionalProfile.css";

const ProfessionalProfile = () => {
  const SHOW_PUBLIC_GALLERY = false;
  const { slug, businessSlug, locationSlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [professional, setProfessional] = useState(null);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [publicNotice, setPublicNotice] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [galleryModal, setGalleryModal] = useState({
    isOpen: false,
    currentIndex: 0,
  });
  const [looks, setLooks] = useState([]);
  const [schedule, setSchedule] = useState(null);

  // Item modal state
  const [itemModal, setItemModal] = useState({
    isOpen: false,
    itemType: "product",
    itemId: null,
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!galleryModal.isOpen) return;

      if (e.key === "Escape") {
        setGalleryModal({ isOpen: false, currentIndex: 0 });
      } else if (e.key === "ArrowLeft" && galleryModal.currentIndex > 0) {
        setGalleryModal((prev) => ({
          ...prev,
          currentIndex: prev.currentIndex - 1,
        }));
      } else if (
        e.key === "ArrowRight" &&
        professional?.portfolioImages &&
        galleryModal.currentIndex < professional.portfolioImages.length - 1
      ) {
        setGalleryModal((prev) => ({
          ...prev,
          currentIndex: prev.currentIndex + 1,
        }));
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [galleryModal, professional?.portfolioImages]);

  useEffect(() => {
    fetchProfessionalData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, businessSlug, locationSlug]);

  useEffect(() => {
    if (!products.length) return;

    const shouldScrollToProducts =
      window.location.hash === "#products-section" ||
      searchParams.get("section") === "products";

    if (!shouldScrollToProducts) return;

    const timer = window.setTimeout(() => {
      document.getElementById("products-section")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 0);

    return () => window.clearTimeout(timer);
  }, [products.length, searchParams]);

  const fetchProfessionalData = async () => {
    try {
      setLoading(true);
      setPublicNotice(null);
      setError("");

      // Fetch professional data by slug
      const profileUrl = `${process.env.REACT_APP_API_BASE_URL}/api/staff/slug/${slug}`;

      const profileRes = await fetch(profileUrl);

      if (profileRes.status === 301) {
        // Handle redirect for old slug
        const redirectData = await profileRes.json();
        if (redirectData.redirect && redirectData.newSlug) {
          // Redirect to new URL
          const newPath =
            redirectData.role === "R7"
              ? `/freelancers/${redirectData.newSlug}`
              : redirectData.role === "R4"
                ? `/freelancer/${redirectData.newSlug}`
                : businessSlug && locationSlug
                  ? `/business/${businessSlug}/${locationSlug}/professional/${redirectData.newSlug}`
                  : `/professional/${redirectData.newSlug}`;
          navigate(newPath, { replace: true });
          return;
        }
      }

      if (!profileRes.ok) {
        let errorPayload = null;
        try {
          errorPayload = await profileRes.json();
        } catch (_err) {}

        if (
          errorPayload?.registrationInProgress ||
          errorPayload?.error === "registration_in_progress"
        ) {
          setPublicNotice({
            title: errorPayload?.title || "Professional setup in progress",
            message:
              errorPayload?.message ||
              "Your favorite professional is still registering. Please check back in a few hours.",
            ownerMessage: errorPayload?.ownerMessage || "",
            loginPath: errorPayload?.loginPath || "/login",
          });
          return;
        }

        throw new Error(errorPayload?.message || "Professional not found");
      }

      const profileData = await profileRes.json();
      setProfessional(profileData);
      setServices(profileData.services || []);

      // Fetch work hours
      try {
        const scheduleUrl = `${process.env.REACT_APP_API_BASE_URL}/api/staff/${profileData._id}/schedule/public`;
        const scheduleRes = await fetch(scheduleUrl);
        if (scheduleRes.ok) {
          const scheduleData = await scheduleRes.json();
          if (scheduleData.success && scheduleData.schedule) {
            setSchedule(scheduleData.schedule);
          }
        }
      } catch (err) {}

      // Fetch featured products
      try {
        const productsRes = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/staff/${profileData._id}/products/public`,
        );
        if (productsRes.ok) {
          const productsData = await productsRes.json();
          const sortedProducts = (productsData.products || [])
            .filter((p) => p.inStock)
            .slice(0, 6);
          setProducts(sortedProducts);
        }
      } catch (err) {}

      // Fetch published looks for this professional
      try {
        const looksRes = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/looks/public?staffId=${profileData._id}&limit=60`,
        );
        if (looksRes.ok) {
          const looksData = await looksRes.json();
          setLooks(looksData.looks || []);
        }
      } catch (err) {}
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleServiceBook = (service, look = null) => {
    // Skip professional selection, go directly to booking with service pre-selected
    const lookIdParam = look?._id
      ? `&lookId=${encodeURIComponent(String(look._id))}`
      : "";
    if (businessSlug && locationSlug) {
      const shopId =
        professional.shopId ||
        professional.activeLocationId ||
        professional.shopIds?.[0]?._id ||
        professional.shopIds?.[0];
      navigate(
        `/booking?shopId=${shopId}&staffId=${professional._id}&serviceId=${service._id}${lookIdParam}`,
      );
    } else {
      navigate(
        `/freelancer-booking?staffId=${professional._id}&serviceId=${service._id}${lookIdParam}`,
      );
    }
  };

  const getLookImages = (look) => {
    if (Array.isArray(look?.images) && look.images.length > 0) {
      return look.images;
    }

    if (look?.image?.url) {
      return [{ url: look.image.url }];
    }

    return [];
  };

  const handleLookBookService = (look) => {
    const serviceId = String(look?.serviceId || "");
    const matchedService = services.find(
      (service) => String(service._id) === serviceId,
    );
    if (matchedService) {
      handleServiceBook(matchedService, look);
      return;
    }

    const serviceName = look?.bookingSnapshot?.serviceName || "";
    if (businessSlug && locationSlug) {
      navigate(
        `/business/${businessSlug}/${locationSlug}/book?serviceName=${encodeURIComponent(serviceName)}`,
      );
      return;
    }

    navigate(`/freelancer-booking?staffId=${professional._id}`);
  };

  const handleShopProductsClick = () => {
    const independentSlug = String(professional?.slug || "").trim();

    if (
      (professional?.role === "R4" || professional?.role === "R7") &&
      independentSlug
    ) {
      navigate(`/shop/${encodeURIComponent(independentSlug)}/products`);
      return;
    }

    const directShopPath = getDirectShopCataloguePath(professional);

    if (
      (professional?.role === "R4" || professional?.role === "R7") &&
      directShopPath
    ) {
      navigate(directShopPath);
      return;
    }

    document
      .getElementById("products-section")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  const headshotAccentColor = useMemo(() => {
    const colorPool = [
      "rgba(59, 130, 246, 0.9)",
      "rgba(56, 189, 248, 0.9)",
      "rgba(96, 165, 250, 0.9)",
      "rgba(37, 99, 235, 0.9)",
      "rgba(14, 165, 233, 0.9)",
    ];
    const idx = slug
      ? slug.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0) % colorPool.length
      : 0;
    return colorPool[idx];
  }, [slug]);

  if (loading) {
    return (
      <>
        <CustomerHeader />
        <div
          style={{
            padding: "60px 20px",
            textAlign: "center",
            minHeight: "60vh",
          }}
        >
          <div style={{ fontSize: "18px", color: "#6b7280" }}>
            Loading professional information...
          </div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  const hasSession = Boolean(localStorage.getItem("ogestyle_token"));

  if (publicNotice && !professional) {
    return (
      <>
        <CustomerHeader />
        <div
          style={{
            padding: "60px 20px",
            textAlign: "center",
            minHeight: "60vh",
            maxWidth: "760px",
            margin: "0 auto",
          }}
        >
          <div
            style={{
              background: "#f8fbff",
              border: "1px solid #bfdbfe",
              borderRadius: "12px",
              padding: "24px",
            }}
          >
            <div
              style={{
                fontSize: "22px",
                fontWeight: "700",
                color: "#1e3a8a",
                marginBottom: "10px",
              }}
            >
              {publicNotice.title}
            </div>
            <div
              style={{
                fontSize: "16px",
                color: "#334155",
                marginBottom: "10px",
              }}
            >
              {publicNotice.message}
            </div>
            {publicNotice.ownerMessage && (
              <div
                style={{
                  fontSize: "14px",
                  color: "#0f766e",
                  marginBottom: "18px",
                }}
              >
                {publicNotice.ownerMessage}
              </div>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "10px",
                flexWrap: "wrap",
              }}
            >
              {!hasSession && (
                <button
                  onClick={() => navigate(publicNotice.loginPath || "/login")}
                  style={{
                    padding: "10px 18px",
                    background: "#1d4ed8",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontWeight: "600",
                  }}
                >
                  Log In to Finish Registration
                </button>
              )}
              <button
                onClick={() => navigate("/")}
                style={{
                  padding: "10px 18px",
                  background: "#e2e8f0",
                  color: "#0f172a",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: "600",
                }}
              >
                Return to Home
              </button>
            </div>
          </div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  if (error || !professional) {
    return (
      <>
        <CustomerHeader />
        <div
          style={{
            padding: "60px 20px",
            textAlign: "center",
            minHeight: "60vh",
          }}
        >
          <div
            style={{ fontSize: "18px", color: "#ef4444", marginBottom: "20px" }}
          >
            {error || "Professional not found"}
          </div>
          <button
            onClick={() => navigate("/")}
            style={{
              padding: "12px 24px",
              background: "#2d6da3",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: "600",
            }}
          >
            Return to Home
          </button>
        </div>
        <CustomerFooter />
      </>
    );
  }

  const personalDisplayName =
    `${professional.firstName || ""} ${professional.lastName || ""}`.trim();
  const profileDisplayName =
    personalDisplayName || professional.businessName || "Professional";
  const isFreelancerStorefront =
    professional.role === "R4" || professional.role === "R7";
  const storefrontDisplayName =
    professional.storefrontDisplayName ||
    professional.businessName ||
    profileDisplayName;
  const storefrontLogo =
    professional.headshotImage ||
    professional.profileImage ||
    professional.userId?.profilePicture;
  const storefrontCity =
    professional.city || professional.location?.city || "Location";
  const storefrontState = professional.state || professional.location?.state || "N/A";
  const storefrontPhone = professional.userId?.phone || professional.phone;
  const storefrontEmail = professional.userId?.email;
  const hasProducts = products.length > 0;
  const heroBackgroundImage =
    professional.backgroundImage || professional.portfolioImages?.[0] || null;
  const shouldShowHeadshotAccent = ["R3", "R4", "R7"].includes(
    professional?.role,
  );

  return (
    <>
      <CustomerHeader />
      <div className="shop-landing-container">
        {/* Hero Section */}
        <div
          className="shop-hero"
          style={{
            background:
              heroBackgroundImage
                ? `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url("${heroBackgroundImage}") center/cover`
                : "var(--ogestyle-storefront-hero-blue)",
            // Height mirrors the shop hero (ShopHero.js): content-driven on mobile, 240px minimum on
            // desktop. It used to be a hard 360px box here and 240px on the shop page, which is why the
            // professional pages looked like a different site.
            height: "auto",
            minHeight: isMobile ? "0" : "240px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            position: "relative",
            overflowX: "hidden",
            padding: isMobile ? "28px 20px 104px" : "26px 60px",
            // The site header is fixed with a 50px bar; mobile used to add 0px and hid the top of the hero.
            marginTop: "calc(var(--demo-banner-height, 0px) + 50px)",
          }}
        >
          {/* Mobile Layout */}
          {isMobile ? (
            isFreelancerStorefront ? (
              <div
                style={{
                  width: "100%",
                  maxWidth: "420px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  position: "relative",
                  zIndex: 1,
                  boxSizing: "border-box",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "12px",
                    position: "relative",
                    zIndex: 1,
                    marginTop: "10px",
                    padding: "0 8px",
                    transform: "translateY(12px)",
                  }}
                >
                  {storefrontLogo && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          width: "96px",
                          height: "96px",
                          position: "relative",
                          transform: "translateY(22px)",
                        }}
                      >
                        <img
                          src={storefrontLogo}
                          alt={storefrontDisplayName}
                          style={{
                            width: "96px",
                            height: "96px",
                            borderRadius: "50%",
                            objectFit: "cover",
                            objectPosition: "center",
                            background: "rgba(255,255,255,0.35)",
                            padding: "2px",
                            border: shouldShowHeadshotAccent
                              ? `3px solid ${headshotAccentColor}`
                              : "1px solid rgba(255,255,255,0.45)",
                            boxShadow: shouldShowHeadshotAccent
                              ? `0 4px 12px rgba(0,0,0,0.3), inset 0 0 10px ${headshotAccentColor}`
                              : "0 4px 12px rgba(0,0,0,0.3)",
                            position: "relative",
                            zIndex: 1,
                          }}
                        />
                      </div>
                    </div>
                  )}

                  <div style={{ textAlign: "center", width: "100%" }}>
                    <h1
                      style={{
                        fontSize: "28px",
                        fontWeight: "700",
                        marginBottom: "2px",
                        textShadow: "0 2px 4px rgba(0,0,0,0.5)",
                        lineHeight: "1.15",
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      {storefrontDisplayName}
                    </h1>
                    <p
                      style={{
                        fontSize: "15px",
                        margin: "0 0 2px 0",
                        textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {storefrontCity}, {storefrontState}
                    </p>
                    {(storefrontPhone || storefrontEmail) && (
                      <div
                        style={{
                          fontSize: "12px",
                          textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                          maxWidth: "100%",
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                          padding: "0 8px",
                          display: "flex",
                          flexDirection: "row",
                          flexWrap: "nowrap",
                          gap: "10px",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {storefrontPhone && (
                          <span style={{ whiteSpace: "nowrap" }}>
                            📞 {storefrontPhone}
                          </span>
                        )}
                        {storefrontEmail && (
                          <span style={{ whiteSpace: "nowrap" }}>
                            ✉️ {storefrontEmail}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "12px",
                      marginTop: "10px",
                      justifyContent: "center",
                      alignItems: "center",
                      flexWrap: "wrap",
                      width: "100%",
                      transform: "translateY(-4px)",
                    }}
                  >
                    {hasProducts && (
                      <button
                        onClick={handleShopProductsClick}
                        style={{
                          padding: "8px 12px",
                          background: "white",
                          color: "#2d6da3",
                          border: "2px solid white",
                          borderRadius: "10px",
                          fontSize: "12px",
                          fontWeight: "700",
                          cursor: "pointer",
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                          transition: "all 0.3s",
                          width: "min(100%, 220px)",
                          whiteSpace: "normal",
                          lineHeight: "1.2",
                        }}
                      >
                        🛍️ Shop Catalogue
                      </button>
                    )}
                  </div>
                </div>

                {schedule && Object.keys(schedule).length > 0 && (
                  <div
                    style={{
                      background: "rgba(255, 255, 255, 0.95)",
                      backdropFilter: "blur(10px)",
                      borderRadius: "8px",
                      padding: "8px 12px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                      width: "100%",
                      maxWidth: "420px",
                      marginTop: "8px",
                      boxSizing: "border-box",
                      transform: "translateY(8px)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "9px",
                        fontWeight: "700",
                        color: "#111827",
                        marginBottom: "4px",
                        textAlign: "center",
                      }}
                    >
                      🕐 Business Hours
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(4, 1fr)",
                        gap: "4px 8px",
                        fontSize: "10px",
                      }}
                    >
                      {Object.entries(schedule).map(([day, hours]) => (
                        <span
                          key={day}
                          style={{
                            color: !hours.isAvailable ? "#ef4444" : "#10b981",
                            fontWeight: "600",
                            whiteSpace: "nowrap",
                            textAlign: "center",
                          }}
                        >
                          {day.substring(0, 3)}:{" "}
                          {!hours.isAvailable
                            ? "Closed"
                            : `${hours.start}-${hours.end}`}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "10px",
                  position: "relative",
                  zIndex: 1,
                  paddingTop: "20px",
                }}
              >
                {/* Logo/Profile Image */}
                {(professional.headshotImage ||
                  professional.profileImage ||
                  professional.userId?.profilePicture) && (
                  <div
                    style={{
                      width: "120px",
                      height: "120px",
                      marginTop: "20px",
                      position: "relative",
                      transform: "translateY(8px)",
                    }}
                  >
                    <img
                      src={
                        professional.headshotImage ||
                        professional.profileImage ||
                        professional.userId?.profilePicture
                      }
                      alt={profileDisplayName}
                      style={{
                        width: "120px",
                        height: "120px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        objectPosition: "center",
                        border: shouldShowHeadshotAccent
                          ? `4px solid ${headshotAccentColor}`
                          : "4px solid white",
                        boxShadow: shouldShowHeadshotAccent
                          ? `0 4px 12px rgba(0,0,0,0.3), inset 0 0 12px ${headshotAccentColor}`
                          : "0 4px 12px rgba(0,0,0,0.3)",
                        position: "relative",
                        zIndex: 1,
                      }}
                    />
                  </div>
                )}

                {/* Business/Professional Name */}
                <h1
                  style={{
                    fontSize: "28px",
                    fontWeight: "700",
                    marginBottom: "0",
                    textShadow: "0 2px 4px rgba(0,0,0,0.5)",
                    textAlign: "center",
                  }}
                >
                  {profileDisplayName}
                </h1>

                {professional?._id && (
                  <div style={{ marginTop: "10px" }}>
                    <FollowButton staffId={professional._id} />
                  </div>
                )}

                {/* Location */}
                <p
                  style={{
                    fontSize: "16px",
                    marginBottom: "0",
                    textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                    textAlign: "center",
                  }}
                >
                  📍 {storefrontCity}, {storefrontState}
                </p>

                {/* Contact Info */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    alignItems: "center",
                    fontSize: "14px",
                    textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                  }}
                >
                  {storefrontPhone && <div>📞 {storefrontPhone}</div>}
                  {storefrontEmail && <div>✉️ {storefrontEmail}</div>}
                </div>

                {/* Buttons */}
                {hasProducts && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "12px",
                      marginTop: "8px",
                      width: "100%",
                      maxWidth: "380px",
                      transform: "translateY(-6px)",
                    }}
                  >
                    <button
                      onClick={handleShopProductsClick}
                      style={{
                        flex: "1",
                        padding: "12px 16px",
                        background: "white",
                        color: "#2d6da3",
                        border: "2px solid white",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontWeight: "700",
                        cursor: "pointer",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      🛍️ Shop Catalogue
                    </button>
                  </div>
                )}

                {/* Business Hours Card */}
                {schedule && Object.keys(schedule).length > 0 && (
                  <div
                    style={{
                      background: "rgba(255, 255, 255, 0.95)",
                      backdropFilter: "blur(10px)",
                      borderRadius: "8px",
                      padding: "8px 12px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                      width: "100%",
                      maxWidth: "95%",
                      marginTop: "8px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "9px",
                        fontWeight: "700",
                        color: "#111827",
                        marginBottom: "4px",
                        textAlign: "center",
                      }}
                    >
                      🕐 Business Hours
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(4, 1fr)",
                        gap: "4px 8px",
                        fontSize: "10px",
                      }}
                    >
                      {Object.entries(schedule).map(([day, hours]) => (
                        <span
                          key={day}
                          style={{
                            color: !hours.isAvailable ? "#ef4444" : "#10b981",
                            fontWeight: "600",
                            whiteSpace: "nowrap",
                            textAlign: "center",
                          }}
                        >
                          {day.substring(0, 3)}:{" "}
                          {!hours.isAvailable
                            ? "Closed"
                            : `${hours.start}-${hours.end}`}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )
          ) : (
            /* Desktop Layout - 3 Columns */
            <div
              style={{
                maxWidth: "1200px",
                width: "100%",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "20px",
                position: "relative",
                zIndex: 1,
              }}
            >
              {/* Left Column - Profile Image (25%) */}
              <div
                style={{
                  flex: "0 0 25%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {(professional.headshotImage ||
                  professional.profileImage ||
                  professional.userId?.profilePicture) && (
                  <div
                    style={{
                      width: "140px",
                      height: "140px",
                      position: "relative",
                    }}
                  >
                    <img
                      src={
                        professional.headshotImage ||
                        professional.profileImage ||
                        professional.userId?.profilePicture
                      }
                      alt={profileDisplayName}
                      style={{
                        width: "140px",
                        height: "140px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        objectPosition: "center",
                        border: shouldShowHeadshotAccent
                          ? `4px solid ${headshotAccentColor}`
                          : "4px solid white",
                        boxShadow: shouldShowHeadshotAccent
                          ? `0 4px 12px rgba(0,0,0,0.3), inset 0 0 14px ${headshotAccentColor}`
                          : "0 4px 12px rgba(0,0,0,0.3)",
                        position: "relative",
                        zIndex: 1,
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Middle Column - Professional Info (50%) */}
              <div
                style={{
                  flex: "0 0 50%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <h1
                  style={{
                    fontSize: "36px",
                    fontWeight: "700",
                    marginBottom: "8px",
                    textShadow: "0 2px 4px rgba(0,0,0,0.5)",
                  }}
                >
                  {profileDisplayName}
                </h1>
                {professional?._id && (
                  <div style={{ marginTop: "4px" }}>
                    <FollowButton staffId={professional._id} />
                  </div>
                )}
                {professional.primaryProfessionalType && (
                  <p
                    style={{
                      fontSize: "18px",
                      marginBottom: "6px",
                      textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                      fontWeight: "500",
                    }}
                  >
                    {formatProfessionalType(
                      professional.primaryProfessionalType,
                    )}
                  </p>
                )}
                <p
                  style={{
                    fontSize: "14px",
                    marginBottom: "4px",
                    textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                  }}
                >
                  📍{" "}
                  {professional.city ||
                    professional.location?.city ||
                    "Location"}
                  ,{" "}
                  {professional.state || professional.location?.state || "N/A"}
                </p>
                {professional.businessName && (
                  <p
                    style={{
                      fontSize: "14px",
                      marginBottom: "4px",
                      textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                    }}
                  >
                    🏢 {professional.businessName}
                  </p>
                )}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "16px",
                    justifyContent: "center",
                    alignItems: "center",
                    fontSize: "14px",
                    textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                  }}
                >
                  {(professional.userId?.phone || professional.phone) && (
                    <span>
                      📞 {professional.userId?.phone || professional.phone}
                    </span>
                  )}
                  {professional.userId?.email && (
                    <span>✉️ {professional.userId.email}</span>
                  )}
                </div>

                {hasProducts && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "12px",
                      marginTop: "16px",
                      justifyContent: "center",
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <button
                      onClick={handleShopProductsClick}
                      style={{
                        padding: "10px 24px",
                        background: "white",
                        color: "#2d6da3",
                        border: "2px solid white",
                        borderRadius: "12px",
                        fontSize: "14px",
                        fontWeight: "700",
                        cursor: "pointer",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                        transition: "all 0.3s",
                        whiteSpace: "nowrap",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow =
                          "0 6px 16px rgba(0, 0, 0, 0.3)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow =
                          "0 4px 12px rgba(0, 0, 0, 0.2)";
                      }}
                    >
                      🛍️ Shop Products
                    </button>
                  </div>
                )}
              </div>

              {/* Right Column - Schedule Availability (25%) */}
              <div
                style={{
                  flex: "0 0 25%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {schedule && Object.keys(schedule).length > 0 && (
                  <div
                    style={{
                      background: "rgba(255, 255, 255, 0.95)",
                      backdropFilter: "blur(10px)",
                      borderRadius: "12px",
                      padding: "16px",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "700",
                        color: "#111827",
                        marginBottom: "12px",
                        borderBottom: "2px solid #2d6da3",
                        paddingBottom: "6px",
                        textAlign: "center",
                      }}
                    >
                      🕐 Availability
                    </div>
                    {Object.entries(schedule).map(([day, hours]) => (
                      <div
                        key={day}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "4px 0",
                          fontSize: "11px",
                        }}
                      >
                        <span
                          style={{
                            fontWeight: "600",
                            textTransform: "capitalize",
                            color: "#111827",
                          }}
                        >
                          {day.substring(0, 3)}
                        </span>
                        <span
                          style={{
                            color: !hours.isAvailable ? "#ef4444" : "#10b981",
                            fontWeight: "500",
                          }}
                        >
                          {!hours.isAvailable
                            ? "Closed"
                            : `${hours.start} - ${hours.end}`}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* About Section */}
        {professional.bio && (
          <div
            style={{
              maxWidth: "1200px",
              margin: "0 auto",
              padding: isMobile ? "10px 16px" : "10px 24px",
            }}
          >
            <section>
              <h2
                className="professional-section-title"
                style={{
                  "--section-title-size": isMobile ? "12px" : "13px",
                  fontSize: isMobile ? "13px" : "15px",
                  fontWeight: "700",
                  marginBottom: "8px",
                  color: "#111827",
                }}
              >
                About
              </h2>
              <p
                style={{
                  fontSize: isMobile ? "12px" : "13px",
                  color: "#6b7280",
                  lineHeight: "1.6",
                }}
              >
                {professional.bio}
              </p>
            </section>
          </div>
        )}

        {/* Services - Responsive Grid */}
        <div
          style={{
            padding: isMobile ? "8px 16px 40px" : "20px 48px 60px",
            background: "#f9fafb",
            boxSizing: "border-box",
          }}
        >
          <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
            {/* Header */}
            <div
              style={{
                marginBottom: isMobile ? "16px" : "24px",
              }}
            >
              <h2
                className="professional-section-title"
                style={{
                  "--section-title-size": isMobile ? "12px" : "13px",
                  fontWeight: "700",
                  marginBottom: "8px",
                  color: "#111827",
                  lineHeight: "1.2",
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Services Offered
              </h2>
              <p
                style={{
                  fontSize: isMobile ? "12px" : "13px",
                  color: "#6b7280",
                  lineHeight: "1.4",
                  margin: 0,
                }}
              >
                All services offered by {profileDisplayName}
              </p>
            </div>

            {/* Responsive Grid */}
            {services.length === 0 ? (
              <p
                style={{
                  margin: 0,
                  color: "#6b7280",
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                No services available yet. Check back soon!
              </p>
            ) : (
              <ul className="service-tiles">
                {services.map((service) => (
                  <li key={service._id}>
                    <ServiceCard
                      service={service}
                      businessType={professional.primaryProfessionalType}
                      onSelect={() => handleServiceBook(service)}
                      ctaLabel="Book Now"
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Looks Section */}
        {looks.length > 0 && (
          <LooksGallery
            looks={looks}
            sectionTitle="✨ Recent Looks"
            isMobile={isMobile}
            onBookService={handleLookBookService}
            viewAllPath={
              professional?._id
                ? `/looks/all?staffId=${professional._id}`
                : null
            }
            navigate={navigate}
          />
        )}

        {/* Products Section */}
        {hasProducts && (
          <div
            id="products-section"
            style={{
              padding: isMobile ? "8px 16px 40px" : "20px 48px 60px",
              background: "white",
              boxSizing: "border-box",
            }}
          >
            <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
              {/* Header */}
              <div
                style={{
                  marginBottom: isMobile ? "12px" : "14px",
                }}
              >
                {isMobile ? (
                  <h3
                    className="professional-section-title"
                    style={{
                      "--section-title-size": isMobile ? "12px" : "13px",
                      fontWeight: "700",
                      marginBottom: "2px",
                      color: "#111827",
                      lineHeight: "1.2",
                      fontSize: "13px",
                    }}
                  >
                    🛍️ Featured Products
                  </h3>
                ) : (
                  <h2
                    className="professional-section-title"
                    style={{
                      "--section-title-size": isMobile ? "12px" : "13px",
                      fontWeight: "700",
                      marginBottom: "2px",
                      color: "#111827",
                      lineHeight: "1.2",
                      fontSize: "15px",
                    }}
                  >
                    🛍️ Featured Products
                  </h2>
                )}
                <p
                  style={{
                    fontSize: isMobile ? "12px" : "13px",
                    color: "#6b7280",
                    lineHeight: "1.4",
                    margin: 0,
                  }}
                >
                  Premium products handpicked by our professional
                </p>
              </div>

              {/* Horizontal scrollable cards */}
              <div
                style={{
                  display: "flex",
                  gap: isMobile ? "10px" : "18px",
                  overflowX: "auto",
                  overflowY: "visible",
                  paddingBottom: "10px",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  flex: 1,
                }}
              >
                {products.map((product) => (
                  <div
                    key={product._id}
                    style={{
                      minWidth: isMobile ? "155px" : "220px",
                      width: isMobile ? "155px" : "220px",
                      height: isMobile ? "200px" : "260px",
                      background: "white",
                      borderRadius: "12px",
                      overflow: "hidden",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      cursor: "pointer",
                      position: "relative",
                      border: "1px solid #e5e7eb",
                      display: "flex",
                      flexDirection: "column",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.boxShadow =
                        "0 12px 32px rgba(0,0,0,0.12)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 16px rgba(0,0,0,0.08)";
                    }}
                    onClick={() =>
                      setItemModal({
                        isOpen: true,
                        itemType: "product",
                        itemId: product._id,
                      })
                    }
                  >
                    {/* Product Image */}
                    <div
                      style={{
                        height: isMobile ? "110px" : "150px",
                        background: product.images?.[0]
                          ? `url("${product.images[0]}") center/cover`
                          : "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)",
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {!product.images?.[0] && (
                        <span style={{ fontSize: "56px", opacity: 0.3 }}>
                          📦
                        </span>
                      )}
                      {!product.inStock && (
                        <div
                          style={{
                            position: "absolute",
                            top: "12px",
                            right: "12px",
                            background: "#ef4444",
                            color: "white",
                            padding: "8px 16px",
                            borderRadius: "8px",
                            fontSize: "12px",
                            fontWeight: "600",
                            boxShadow: "0 2px 8px rgba(239, 68, 68, 0.3)",
                          }}
                        >
                          Out of Stock
                        </div>
                      )}
                    </div>

                    {/* Product Details */}
                    <div
                      style={{
                        padding: isMobile ? "8px" : "12px",
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                      }}
                    >
                      <h3
                        style={{
                          fontSize: isMobile ? "11px" : "13px",
                          fontWeight: "600",
                          margin: 0,
                          color: "#111827",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {product.name}
                      </h3>

                      <p
                        style={{
                          fontSize: isMobile ? "10px" : "11px",
                          color: "#6b7280",
                          margin: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {product.description || "Quality product"}
                      </p>

                      {/* Price and Stock - pushed to bottom */}
                      <div style={{ marginTop: "auto" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "8px",
                            marginBottom: "4px",
                          }}
                        >
                          <h2
                            style={{
                              fontWeight: "700",
                              color: "#059669",
                              margin: 0,
                              fontSize: isMobile ? "11px" : "12px",
                              lineHeight: 1.1,
                            }}
                          >
                            ${product.price?.toFixed(2) || "0.00"}
                          </h2>
                          {product.inStock && product.stock && (
                            <div
                              style={{
                                fontSize: isMobile ? "10px" : "11px",
                                fontWeight: "600",
                                color: "#2563eb",
                                lineHeight: 1.1,
                              }}
                            >
                              {product.stock} in stock
                            </div>
                          )}
                        </div>

                        {/* View Button */}
                        <h3
                          style={{
                            width: "100%",
                            padding: "6px",
                            background: "#2d6da3",
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            fontWeight: "600",
                            cursor: "pointer",
                            transition: "background 0.2s",
                            textAlign: "center",
                            margin: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxSizing: "border-box",
                            fontSize: "13px",
                          }}
                          onMouseEnter={(e) =>
                            (e.target.style.background = "#1e5a8a")
                          }
                          onMouseLeave={(e) =>
                            (e.target.style.background = "#2d6da3")
                          }
                        >
                          View Product
                        </h3>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Gallery - Hidden for now */}
        <div
          style={{
            display: "none",
            maxWidth: "1200px",
            margin: "0 auto",
            padding: isMobile ? "40px 16px" : "60px 24px",
          }}
        >
          {professional.portfolioImages &&
            professional.portfolioImages.length > 1 && (
              <section style={{ marginBottom: isMobile ? "40px" : "60px" }}>
                <h2
                  style={{
                    fontSize: isMobile ? "24px" : "32px",
                    fontWeight: "700",
                    marginBottom: "20px",
                    color: "#111827",
                  }}
                >
                  📸 Portfolio
                </h2>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "repeat(2, 1fr)"
                      : "repeat(3, 1fr)",
                    gap: "16px",
                  }}
                >
                  {professional.portfolioImages.map((img, idx) => (
                    <div
                      key={idx}
                      onClick={() =>
                        setGalleryModal({ isOpen: true, currentIndex: idx })
                      }
                      style={{
                        height: isMobile ? "150px" : "200px",
                        borderRadius: "12px",
                        overflow: "hidden",
                        background: `url("${img}") center/cover`,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        cursor: "pointer",
                        transition: "transform 0.2s, box-shadow 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.05)";
                        e.currentTarget.style.boxShadow =
                          "0 4px 16px rgba(0,0,0,0.2)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                        e.currentTarget.style.boxShadow =
                          "0 2px 8px rgba(0,0,0,0.1)";
                      }}
                    />
                  ))}
                </div>
              </section>
            )}
        </div>
      </div>

      {/* Gallery Modal - Hidden for now */}
      {SHOW_PUBLIC_GALLERY &&
        galleryModal.isOpen &&
        professional.portfolioImages && (
          <div
            style={{
              display: "none",
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.9)",
              zIndex: 9999,
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
            }}
            onClick={() => setGalleryModal({ isOpen: false, currentIndex: 0 })}
          >
            <div
              style={{
                position: "relative",
                maxWidth: "90vw",
                maxHeight: "90vh",
                width: isMobile ? "100%" : "auto",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() =>
                  setGalleryModal({ isOpen: false, currentIndex: 0 })
                }
                style={{
                  position: "absolute",
                  top: "-40px",
                  right: "0",
                  background: "white",
                  border: "none",
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  fontSize: "24px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 10000,
                }}
              >
                ×
              </button>

              <img
                src={professional.portfolioImages[galleryModal.currentIndex]}
                alt={`Portfolio ${galleryModal.currentIndex + 1}`}
                style={{
                  width: "100%",
                  height: isMobile ? "60vh" : "80vh",
                  objectFit: "contain",
                  borderRadius: "8px",
                }}
              />

              {professional.portfolioImages.length > 1 && (
                <>
                  {galleryModal.currentIndex > 0 && (
                    <button
                      onClick={() =>
                        setGalleryModal((prev) => ({
                          ...prev,
                          currentIndex: prev.currentIndex - 1,
                        }))
                      }
                      style={{
                        position: "absolute",
                        left: isMobile ? "10px" : "-60px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "white",
                        border: "none",
                        borderRadius: "50%",
                        width: "50px",
                        height: "50px",
                        fontSize: "24px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                      }}
                    >
                      ←
                    </button>
                  )}

                  {galleryModal.currentIndex <
                    professional.portfolioImages.length - 1 && (
                    <button
                      onClick={() =>
                        setGalleryModal((prev) => ({
                          ...prev,
                          currentIndex: prev.currentIndex + 1,
                        }))
                      }
                      style={{
                        position: "absolute",
                        right: isMobile ? "10px" : "-60px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "white",
                        border: "none",
                        borderRadius: "50%",
                        width: "50px",
                        height: "50px",
                        fontSize: "24px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                      }}
                    >
                      →
                    </button>
                  )}

                  <div
                    style={{
                      position: "absolute",
                      bottom: "-40px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      color: "white",
                      fontSize: "14px",
                      fontWeight: "600",
                    }}
                  >
                    {galleryModal.currentIndex + 1} /{" "}
                    {professional.portfolioImages.length}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

      {/* Reviews Section - Phase G */}
      {professional?._id && (
        <div
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            padding: isMobile ? "20px 16px 40px" : "40px 20px 60px",
          }}
        >
          <ReviewsDisplay
            type="professional"
            id={professional._id}
            showResponseUI={false}
          />
        </div>
      )}

      <CustomerFooter />

      {/* Item Detail Modal */}
      <ItemDetailModal
        isOpen={itemModal.isOpen}
        onClose={() =>
          setItemModal({ isOpen: false, itemType: "product", itemId: null })
        }
        itemType={itemModal.itemType}
        itemId={itemModal.itemId}
        ownerType="professional"
        ownerId={professional?._id}
      />
    </>
  );
};

export default ProfessionalProfile;